import { ReactNode } from "react";
import styles from "../FlashOfferBanner/FlashOfferBanner.module.scss";
import { BottomRightCornerWave } from "../Icons/Icons";

type PromoBannerCardProps = {
  /** The badge at the card's start — an offer's icon motif, or an announcement disc. */
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** The single button, in the slot the offer banner's Apply occupies. */
  action?: ReactNode;
  /**
   * Sits under the title — the offer banner's "unavailable" chip. Carries its
   * own test id because callers are asserted on by it.
   */
  chip?: { text: ReactNode; testId?: string };
  /**
   * The offer banner's expiry line. Rendered here rather than passed in as a
   * node because its label span carries its own weight in the shared module.
   * `label` is omitted when the merchant authored the whole line themselves.
   */
  expiry?: { label?: string; value: string; testId?: string };
  /**
   * Truncate the description past two lines. The loyalty offer banner's guide
   * asks for it; opt-in so the announcement card keeps its own copy intact.
   */
  clampDescription?: boolean;
  testId?: string;
  /** Applied/redeemed dress. */
  active?: boolean;
  unavailable?: boolean;
  organization?: boolean;
  /** Sizes the copy as the tiers design does, which sets the card's height. */
  tierSized?: boolean;
};

/**
 * The home rail's card skeleton, shared by every banner the rail can show.
 *
 * It exists because the shape is five levels of nested SCSS
 * (`.slide > .slide_wrapper > .slide_details > .offer_details > …`): a second
 * card type reproducing that by hand would drift from the first the moment
 * either changed. The structure lives here; what goes in each slot is the
 * caller's business, which is why `title` and `description` are nodes rather
 * than strings — the offer card renders authored HTML into them, the
 * announcement card renders plain text.
 */
export const PromoBannerCard = ({
  icon,
  title,
  description,
  action,
  chip,
  expiry,
  clampDescription,
  testId,
  active,
  unavailable,
  organization,
  tierSized,
}: PromoBannerCardProps) => {
  const modifiers = [
    organization && styles.organization_offer,
    active && styles.active_slide,
    unavailable && styles.unavailable,
    tierSized && styles.tier_sized,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      data-testid={testId}
      className={`${styles.slide} ${modifiers} bg__12 rounded`}
    >
      <div className={styles.slide_wrapper}>
        <div className={styles.slide_details}>
          {icon}
          <div className={styles.offer_details}>
            <div className={styles.offer_action}>{action}</div>
            <p className={styles.offer_title}>{title}</p>
            {chip && (
              <div data-testid={chip.testId} className={styles.unavailable}>
                {chip.text}
              </div>
            )}
            <div className={styles.offer_desc_wrapper}>
              {description && (
                <p
                  className={`${styles.offer_desc} ${
                    clampDescription ? styles.clamped : ""
                  }`}
                >
                  {description}
                </p>
              )}
              {expiry && (
                <p className={styles.expiry_date} data-testid={expiry.testId}>
                  {expiry.label && <span>{expiry.label} </span>}
                  {expiry.value}
                </p>
              )}
            </div>
          </div>

          <div className={styles.slider_bottom_svg}>
            <BottomRightCornerWave />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * The rail's one button, in the shared module's own class so an offer's
 * Apply/Remove and an announcement's CTA cannot drift apart. Rendered into
 * [PromoBannerCard]'s `action` slot, which supplies its position.
 */
export const PromoBannerActionButton = ({
  onClick,
  children,
  testId,
  variant = "default",
}: {
  onClick: () => void;
  children: ReactNode;
  testId?: string;
  /** `remove` is the applied state's dress. */
  variant?: "default" | "remove";
}) => (
  <button
    className={`${styles.offer_action__btn} ${
      variant === "remove" ? styles.remove_btn : ""
    }`}
    onClick={onClick}
    data-testid={testId}
  >
    {children}
  </button>
);
